import * as pc from "playcanvas";
import { MOVEMENT_FPS } from "@hupper/shared";
import { getWeapon } from "@hupper/shared/weapons";
import type { RemotePlayerState } from "../net/GameClient";
import type { CharacterFactory } from "./CharacterFactory";
import type { HalftimePose, RemoteCharacter } from "./RemoteCharacter";
import { buildWeaponModel } from "./weaponBuilder";
import { buildGrenadeModel } from "./grenadeViewModel";

/**
 * Fallback held-weapon anchor (player root local frame, faces −Z, +X = right).
 * Only used for the brief window before the skinned character + its hand bone
 * are available — once they are, the weapon tracks the actual `mixamorig:
 * RightHand` bone (see GRIP_BY_CLASS + update()).
 */
const WEAPON_HAND_X = 0.12;
const WEAPON_HAND_Y = 1.35;
const WEAPON_HAND_Z = -0.22;
const WEAPON_CROUCH_DROP = 0.32;

/**
 * Per-weapon-class grip so each weapon sits properly in the fist when it's
 * tracking the hand bone:
 *  - `modelPos` shifts the built model so its grip point lands at the holder
 *    origin (= the wrist bone). Derived from each model's grip part: pistols
 *    grip ≈ (0,−0.06,0), rifles ≈ (0,−0.08,+0.13) in model space, so we move
 *    the model by the negation.
 *  - `modelRot` re-points models whose "forward" isn't −Z (the knife's blade
 *    is built along +Y → rotate −90° about X so it points forward).
 *  - `fist*` seats the whole weapon a little forward/into the curled fingers
 *    (the bone is the wrist, behind the grip), in the aim frame.
 */
interface GripCfg {
  modelPos: [number, number, number];
  modelRot: [number, number, number];
  fistFwd: number;
  fistUp: number;
  fistRight: number;
}
const GRIP_BY_CLASS: Record<string, GripCfg> = {
  pistol:  { modelPos: [0, 0.06, 0],     modelRot: [0, 0, 0],   fistFwd: 0.05, fistUp: -0.02, fistRight: 0 },
  smg:     { modelPos: [0, 0.08, -0.10], modelRot: [0, 0, 0],   fistFwd: 0.06, fistUp: -0.02, fistRight: 0 },
  rifle:   { modelPos: [0, 0.08, -0.13], modelRot: [0, 0, 0],   fistFwd: 0.08, fistUp: -0.02, fistRight: 0 },
  sniper:  { modelPos: [0, 0.08, -0.10], modelRot: [0, 0, 0],   fistFwd: 0.08, fistUp: -0.02, fistRight: 0 },
  melee:   { modelPos: [0, -0.02, 0],    modelRot: [-90, 0, 0], fistFwd: 0.04, fistUp: -0.02, fistRight: 0 },
  grenade: { modelPos: [0, 0, 0],        modelRot: [0, 0, 0],   fistFwd: 0.02, fistUp: -0.02, fistRight: 0 },
};
const DEFAULT_GRIP: GripCfg = GRIP_BY_CLASS.pistol;

/**
 * RemotePlayer — opponent body.
 *
 * Prefers a skinned Mixamo character (idle/run/walk/strafe/reload/death
 * driven by the snapshot) via {@link CharacterFactory}. Until the model
 * loads — or if it fails — it falls back to a procedural primitive rig
 * (boxes / capsules / spheres) tinted by team accent. Position/yaw come
 * from the server; the character animation is purely cosmetic (hitboxes
 * are server-side).
 */

export class RemotePlayer {
  readonly sessionId: string;
  private app: pc.AppBase;
  private root: pc.Entity;
  /** Wraps the actual visible body — keeps crouch scale isolated from
   *  root transform so position prediction stays clean. */
  private visualHolder: pc.Entity;
  private placeholder: pc.Entity | null = null;

  private targetX = 0;
  private targetY = 0;
  private targetZ = 0;
  private targetYaw = 0;
  private targetCrouch = 0;
  private crouchAmount = 0;
  private targetPitch = 0;
  private currentPitch = 0;

  /**
   * Snapshot interpolation buffer — one keyframe per 32 Hz server
   * snapshot, keyed by server tick. `update()` renders the body a fixed
   * short interval in the past (GameClient.getRemoteRenderTick) and
   * LINEARLY interpolates between the two keyframes bracketing that
   * time, so remote motion plays back at true server velocity: crisp
   * strafes and stops instead of the old exponential chase toward the
   * newest snapshot (rubbery, frame-rate-dependent smear).
   */
  private interpBuf: Array<{
    tick: number; x: number; y: number; z: number; yaw: number; pitch: number;
  }> = [];
  private static readonly INTERP_BUF_MAX = 64;

  /**
   * Held weapon — a holder parented to root (so it inherits server
   * position + yaw but NOT the crouch Y-squash). The model inside is
   * rebuilt whenever the equipped weaponId changes; `getHandWorld`
   * exposes its world position so peer tracers fire from the gun, not
   * the eye.
   */
  private readonly weaponHolder: pc.Entity;
  /** Inner entity the model is built into — carries the per-class grip
   *  alignment so the holder origin stays the gripping point. */
  private readonly weaponModel: pc.Entity;
  private mountedWeaponId: string | null = null;
  private gripCfg: GripCfg = DEFAULT_GRIP;

  /**
   * Sprint 5 — death animation state. Once health <= 0 we lock the body
   * pose and play a ragdoll-lite slump: rotate 90° forward over 400 ms
   * and translate back ~0.5 m. We also stop reconciling toward server
   * targets so the corpse settles where it died.
   */
  private isDead = false;
  /** Pending match-result pose (see playResult) — null = none. */
  private resultWon: boolean | null = null;
  /** Pending halftime pose (see playHalftime) — null = none. */
  private halftimePose: HalftimePose | null = null;
  private deathPhase = 0;
  private static readonly DEATH_DURATION = 0.4;
  private static readonly DEATH_SLIDE_BACK = 0.5;
  private lastKnownHealth = 100;
  /** Skinned Mixamo character (preferred over the primitive rig). */
  private readonly factory: CharacterFactory | null;
  private character: RemoteCharacter | null = null;
  /** Fixed at construction (tint + placeholder gear) — the manager rebuilds
   *  the body when a snapshot arrives with the other team (halftime swap). */
  readonly team: "ct" | "t";
  /** True when the skinned character is handling visuals (rig hidden). */
  private usingCharacter = false;
  private reloadingFlag = false;
  private walkingFlag = false;
  private knifeFlag = false;
  private crouchFlag = false;
  /** Previous render position, for deriving velocity to drive locomotion. */
  private prevX = 0;
  private prevZ = 0;
  private prevY = 0;
  /** Cached forward at moment of death so the slide stays directional. */
  private deathForwardX = 0;
  private deathForwardZ = -1;
  /** Saved death position so slide is computed relative, not absolute. */
  private deathPosX = 0;
  private deathPosZ = 0;

  constructor(
    app: pc.AppBase,
    sessionId: string,
    initial: RemotePlayerState,
    accent: pc.Color,
    factory: CharacterFactory | null = null,
  ) {
    this.app = app;
    this.sessionId = sessionId;
    this.factory = factory;
    this.team = initial.team || "ct";

    this.root = new pc.Entity(`remote-player-${sessionId}`);
    this.root.setPosition(initial.x, initial.y, initial.z);

    this.visualHolder = new pc.Entity(`remote-visual-${sessionId}`);
    this.root.addChild(this.visualHolder);

    // Held-weapon anchor (sibling of the body, under root → gets server
    // position + yaw, immune to the crouch squash). update() re-homes it onto
    // the animated hand bone once the skinned character loads; until then it
    // sits at the fallback offset. The model is built into an inner entity so
    // the holder origin can stay the gripping point.
    this.weaponHolder = new pc.Entity(`remote-weapon-${sessionId}`);
    this.weaponHolder.setLocalPosition(WEAPON_HAND_X, WEAPON_HAND_Y, WEAPON_HAND_Z);
    this.root.addChild(this.weaponHolder);
    this.weaponModel = new pc.Entity(`remote-weapon-model-${sessionId}`);
    this.weaponHolder.addChild(this.weaponModel);

    // Build the procedural rig synchronously so the silhouette is visible
    // the instant the snapshot arrives. Accent color tints the team
    // identity (red for T side, blue for CT — caller decides).
    this.buildPlaceholder(accent, initial.team || "ct");

    this.targetX = initial.x;
    this.targetY = initial.y;
    this.targetZ = initial.z;
    this.targetYaw = initial.yaw;
    this.targetPitch = initial.pitch ?? 0;
    this.currentPitch = this.targetPitch;
    this.prevX = initial.x;
    this.prevZ = initial.z;
    this.prevY = initial.y;

    // Mount the starter weapon so the body isn't empty-handed on spawn.
    this.syncWeapon(initial.weaponId);

    this.app.root.addChild(this.root);

    // Upgrade to the skinned character if its model is already loaded;
    // otherwise update() retries until the factory is ready.
    this.tryCreateCharacter();
  }

  /**
   * Instantiate the skinned character (once the model has loaded) and hide
   * the primitive rig. Parented under root, so it inherits server
   * position/yaw; a local 180° flip aligns Mixamo's +Z facing with ours.
   */
  private tryCreateCharacter(): void {
    if (this.character || !this.factory?.ready) return;
    const char = this.factory.create(this.team);
    if (!char) return;
    this.character = char;
    this.usingCharacter = true;
    char.entity.setLocalEulerAngles(0, 180, 0);
    char.entity.setLocalPosition(0, 0, 0);
    this.root.addChild(char.entity);
    if (this.placeholder) this.placeholder.enabled = false;
    if (this.isDead) char.die();
    if (this.resultWon !== null) char.playResult(this.resultWon);
    if (this.halftimePose !== null) char.playHalftime(this.halftimePose);
  }

  // ── Placeholder body ─────────────────────────────────────────────────

  private buildPlaceholder(accent: pc.Color, team: "t" | "ct"): void {
    const dark = new pc.Color(accent.r * 0.22, accent.g * 0.22, accent.b * 0.22);
    const trim = new pc.Color(
      Math.min(1, accent.r * 1.15),
      Math.min(1, accent.g * 1.1),
      Math.min(1, accent.b * 1.12),
    );
    const plate = new pc.Color(0.12, 0.13, 0.15);
    const skin = new pc.Color(0.85, 0.65, 0.50); // Skin tone for exposed parts

    const ph = new pc.Entity("placeholder");
    this.placeholder = ph;
    this.visualHolder.addChild(ph);

    if (team === "ct") {
      // ── Counter-Terrorist (CT) Design ──
      // Heavy armor, helmet, goggles, tactical gear, navy blue uniform
      const navy = new pc.Color(0.12, 0.18, 0.28);
      const black = new pc.Color(0.08, 0.08, 0.08);
      const vestColor = new pc.Color(0.15, 0.16, 0.18);
      
      this.addPrimitive(ph, "pelvis",     "box",     [0.42, 0.22, 0.30], [0, 0.85, 0],   navy,  0.12);
      this.addPrimitive(ph, "torso",      "box",     [0.46, 0.45, 0.32], [0, 1.20, 0],   navy, 0.15);
      this.addPrimitive(ph, "vest",       "box",     [0.48, 0.35, 0.34], [0, 1.25, 0.02], vestColor,  0.35); // Heavy tactical vest
      this.addPrimitive(ph, "pouches",    "box",     [0.40, 0.15, 0.10], [0, 1.15, -0.18], black,  0.2);  // Ammo pouches
      this.addPrimitive(ph, "radio",      "box",     [0.08, 0.15, 0.06], [-0.18, 1.25, -0.18], black, 0.1);
      this.addPrimitive(ph, "radio-ant",  "cylinder",[0.01, 0.10, 0.01], [-0.20, 1.35, -0.18], plate, 0.1);
      
      // Neck and Head
      this.addPrimitive(ph, "neck",       "cylinder",[0.12, 0.15, 0.12], [0, 1.48, 0],   black,   0.10); // Balaclava neck
      this.addPrimitive(ph, "head",       "sphere",  [0.24, 0.26, 0.24], [0, 1.65, 0],   black,   0.1);  // Masked head
      this.addPrimitive(ph, "helmet",     "sphere",  [0.29, 0.20, 0.29], [0, 1.70, 0],   vestColor,  0.3);  // Kevlar helmet
      this.addPrimitive(ph, "helmet-brim","box",     [0.30, 0.04, 0.15], [0, 1.68, -0.10], vestColor, 0.3);
      this.addPrimitive(ph, "visor",      "box",     [0.22, 0.08, 0.12], [0, 1.65, -0.12], new pc.Color(0.05, 0.05, 0.1), 0.9); // Dark goggles
      this.addPrimitive(ph, "headset-l",  "box",     [0.06, 0.12, 0.10], [-0.15, 1.65, 0], black, 0.3);
      this.addPrimitive(ph, "headset-r",  "box",     [0.06, 0.12, 0.10], [0.15, 1.65, 0],  black, 0.3);
      
      // Arms
      this.addPrimitive(ph, "shoulder-l", "sphere",  [0.22, 0.22, 0.22], [-0.28, 1.35, 0], navy,  0.15);
      this.addPrimitive(ph, "shoulder-r", "sphere",  [0.22, 0.22, 0.22], [0.28, 1.35, 0],  navy,  0.15);
      this.addPrimitive(ph, "arm-upper-l","capsule", [0.16, 0.35, 0.16], [-0.32, 1.15, 0], navy, 0.15);
      this.addPrimitive(ph, "arm-upper-r","capsule", [0.16, 0.35, 0.16], [0.32, 1.15, 0],  navy, 0.15);
      this.addPrimitive(ph, "arm-lower-l","capsule", [0.14, 0.35, 0.14], [-0.32, 0.85, 0.1], navy, 0.15);
      this.addPrimitive(ph, "arm-lower-r","capsule", [0.14, 0.35, 0.14], [0.32, 0.85, 0.1],  navy, 0.15);
      this.addPrimitive(ph, "glove-l",    "box",     [0.12, 0.15, 0.12], [-0.32, 0.65, 0.15], black, 0.2);
      this.addPrimitive(ph, "glove-r",    "box",     [0.12, 0.15, 0.12], [0.32, 0.65, 0.15], black, 0.2);
      
      // Legs
      this.addPrimitive(ph, "leg-upper-l","capsule", [0.20, 0.45, 0.20], [-0.15, 0.55, 0], navy, 0.15);
      this.addPrimitive(ph, "leg-upper-r","capsule", [0.20, 0.45, 0.20], [0.15, 0.55, 0],  navy, 0.15);
      this.addPrimitive(ph, "knee-pad-l", "box",     [0.18, 0.15, 0.10], [-0.15, 0.35, -0.08], black, 0.2);
      this.addPrimitive(ph, "knee-pad-r", "box",     [0.18, 0.15, 0.10], [0.15, 0.35, -0.08], black, 0.2);
      this.addPrimitive(ph, "leg-lower-l","capsule", [0.16, 0.45, 0.16], [-0.15, 0.20, 0], navy,   0.15);
      this.addPrimitive(ph, "leg-lower-r","capsule", [0.16, 0.45, 0.16], [0.15, 0.20, 0],  navy,   0.15);
      this.addPrimitive(ph, "foot-l",     "box",     [0.16, 0.10, 0.24], [-0.15, 0.05, -0.05], black, 0.1);
      this.addPrimitive(ph, "foot-r",     "box",     [0.16, 0.10, 0.24], [0.15, 0.05, -0.05], black, 0.1);
      
      // Details / Pouches / Tactical Gear
      this.addPrimitive(ph, "belt",       "box",     [0.44, 0.08, 0.32], [0, 0.80, 0], black, 0.1);
      this.addPrimitive(ph, "buckle",     "box",     [0.12, 0.10, 0.34], [0, 0.80, 0], new pc.Color(0.2, 0.2, 0.2), 0.5); // Dark metal buckle
      this.addPrimitive(ph, "holster",    "box",     [0.10, 0.20, 0.15], [0.22, 0.65, 0], black, 0.1);
      this.addPrimitive(ph, "mag-pouch-1","box",     [0.08, 0.15, 0.06], [-0.10, 0.80, -0.18], black, 0.1);
      this.addPrimitive(ph, "mag-pouch-2","box",     [0.08, 0.15, 0.06], [0.0, 0.80, -0.18], black, 0.1);
      this.addPrimitive(ph, "mag-pouch-3","box",     [0.08, 0.15, 0.06], [0.10, 0.80, -0.18], black, 0.1);
      this.addPrimitive(ph, "backpack",   "box",     [0.35, 0.40, 0.15], [0, 1.25, 0.22], black, 0.1);
      this.addPrimitive(ph, "stripe",     "box",     [0.42, 0.06, 0.36], [0, 1.30, 0.02], new pc.Color(0.2, 0.2, 0.2),  0.55);
    } else {
      // ── Terrorist (T) Design ──
      // Casual/rebel clothing, no helmet (unless bought, but visually distinct), sunglasses, exposed skin

      const shirtColor = new pc.Color(0.75, 0.7, 0.6); // Tan/light shirt
      const pantsColor = new pc.Color(0.25, 0.3, 0.35); // Blue jeans
      const harnessColor = new pc.Color(0.2, 0.25, 0.2); // Olive green harness
      const shoeColor = new pc.Color(0.8, 0.8, 0.8); // White sneakers
      
      this.addPrimitive(ph, "pelvis",     "box",     [0.40, 0.20, 0.28], [0, 0.85, 0],   pantsColor, 0.1);
      this.addPrimitive(ph, "torso",      "box",     [0.44, 0.45, 0.30], [0, 1.20, 0],   shirtColor, 0.1);
      this.addPrimitive(ph, "vest",       "box",     [0.46, 0.35, 0.32], [0, 1.25, 0.02], harnessColor,  0.2); // Light rig/harness
      this.addPrimitive(ph, "pouches",    "box",     [0.38, 0.12, 0.08], [0, 1.15, -0.16], dark,  0.1);  // Ammo pouches
      
      // Neck and Head
      this.addPrimitive(ph, "neck",       "cylinder",[0.11, 0.15, 0.11], [0, 1.48, 0],   skin,   0.1); // Exposed neck
      this.addPrimitive(ph, "head",       "sphere",  [0.24, 0.26, 0.24], [0, 1.65, 0],   skin,   0.1); // Exposed head
      this.addPrimitive(ph, "hair",       "box",     [0.25, 0.08, 0.25], [0, 1.76, 0],   new pc.Color(0.15, 0.1, 0.05), 0.1); // Hair
      this.addPrimitive(ph, "sunglasses", "box",     [0.22, 0.06, 0.10], [0, 1.68, -0.11], new pc.Color(0.02, 0.02, 0.02), 0.9); // Aviators
      this.addPrimitive(ph, "bandana",    "box",     [0.25, 0.12, 0.20], [0, 1.55, -0.05], accent, 0.1); // Mask over mouth (team colored)
      
      // Arms (Rolled up sleeves)
      this.addPrimitive(ph, "shoulder-l", "sphere",  [0.20, 0.20, 0.20], [-0.26, 1.35, 0], shirtColor, 0.1);
      this.addPrimitive(ph, "shoulder-r", "sphere",  [0.20, 0.20, 0.20], [0.26, 1.35, 0],  shirtColor, 0.1);
      this.addPrimitive(ph, "arm-upper-l","capsule", [0.14, 0.35, 0.14], [-0.30, 1.15, 0], shirtColor, 0.1);
      this.addPrimitive(ph, "arm-upper-r","capsule", [0.14, 0.35, 0.14], [0.30, 1.15, 0],  shirtColor, 0.1);
      this.addPrimitive(ph, "arm-lower-l","capsule", [0.13, 0.35, 0.13], [-0.30, 0.85, 0.1], skin, 0.1); // Exposed forearms
      this.addPrimitive(ph, "arm-lower-r","capsule", [0.13, 0.35, 0.13], [0.30, 0.85, 0.1],  skin, 0.1);
      this.addPrimitive(ph, "hand-l",     "box",     [0.10, 0.12, 0.10], [-0.30, 0.65, 0.15], skin, 0.1); // Bare hands
      this.addPrimitive(ph, "hand-r",     "box",     [0.10, 0.12, 0.10], [0.30, 0.65, 0.15],  skin, 0.1);
      
      // Belt
      this.addPrimitive(ph, "belt",       "box",     [0.42, 0.08, 0.30], [0, 0.80, 0], new pc.Color(0.1, 0.05, 0.02), 0.1); // Brown leather belt
      this.addPrimitive(ph, "buckle",     "box",     [0.10, 0.10, 0.32], [0, 0.80, 0], new pc.Color(0.8, 0.8, 0.8), 0.8); // Silver buckle
      
      // Legs (Jeans)
      this.addPrimitive(ph, "leg-upper-l","capsule", [0.17, 0.45, 0.17], [-0.15, 0.55, 0], pantsColor, 0.1);
      this.addPrimitive(ph, "leg-upper-r","capsule", [0.17, 0.45, 0.17], [0.15, 0.55, 0],  pantsColor, 0.1);
      this.addPrimitive(ph, "leg-lower-l","capsule", [0.16, 0.45, 0.16], [-0.15, 0.20, 0], pantsColor, 0.1);
      this.addPrimitive(ph, "leg-lower-r","capsule", [0.16, 0.45, 0.16], [0.15, 0.20, 0],  pantsColor, 0.1);
      this.addPrimitive(ph, "foot-l",     "box",     [0.15, 0.10, 0.22], [-0.15, 0.05, -0.05], shoeColor, 0.1); // Sneakers
      this.addPrimitive(ph, "foot-r",     "box",     [0.15, 0.10, 0.22], [0.15, 0.05, -0.05],  shoeColor, 0.1);
    }
  }

  private addPrimitive(
    parent: pc.Entity,
    name: string,
    shape: "box" | "capsule" | "sphere" | "cylinder",
    scale: [number, number, number],
    pos: [number, number, number],
    diffuse: pc.Color,
    gloss: number,
  ): void {
    const e = new pc.Entity(name);
    e.addComponent("render", { type: shape });
    e.setLocalScale(scale[0], scale[1], scale[2]);
    e.setLocalPosition(pos[0], pos[1], pos[2]);
    const mat = new pc.StandardMaterial();
    mat.diffuse = diffuse;
    mat.gloss = gloss;
    mat.update();
    const render = e.render;
    if (render) {
      render.material = mat;
      render.castShadows = false;
      render.receiveShadows = false;
    }
    parent.addChild(e);
  }

  // ── Held weapon ──────────────────────────────────────────────────────

  /**
   * Rebuild the held-weapon model when the equipped weapon changes. Guns
   * + knife route through the shared `buildWeaponModel` (same mesh the
   * shooter sees in first person and the ground item uses); grenades use
   * the grenade view-model. No-op when the id is unchanged. Wrapped in
   * try/catch so a builder hiccup on one weapon can't blank the body.
   */
  private syncWeapon(weaponId: string): void {
    if (weaponId === this.mountedWeaponId) return;
    this.mountedWeaponId = weaponId;
    // Clear the previous model (children of the inner model entity — the
    // holder itself stays so its hand-tracking transform is preserved).
    for (const child of this.weaponModel.children.slice()) child.destroy();
    let cls: string;
    try {
      cls = getWeapon(weaponId).class;
    } catch {
      return; // unknown id — leave hands empty rather than guess
    }
    // Align the model's grip to the holder origin (the hand) + per-class fixups.
    const cfg = GRIP_BY_CLASS[cls] ?? DEFAULT_GRIP;
    this.gripCfg = cfg;
    this.weaponModel.setLocalPosition(cfg.modelPos[0], cfg.modelPos[1], cfg.modelPos[2]);
    this.weaponModel.setLocalEulerAngles(cfg.modelRot[0], cfg.modelRot[1], cfg.modelRot[2]);
    try {
      if (cls === "grenade") buildGrenadeModel(this.weaponModel, weaponId);
      else buildWeaponModel(this.weaponModel, weaponId); // melee / pistol / smg / rifle / sniper
    } catch (err) {
      console.warn(`[RemotePlayer] failed to build held weapon ${weaponId}:`, err);
    }
  }

  /**
   * World position of the opponent's hand/weapon, for peer shot + tracer
   * origins (so bullets read as leaving the gun, not the head). Returns
   * false while dead — the hand isn't holding anything then.
   */
  getHandWorld(out: pc.Vec3): boolean {
    if (this.isDead) return false;
    out.copy(this.weaponHolder.getPosition());
    return true;
  }

  /**
   * World position of the animated hips bone — where the visual body
   * actually is when a clip carries root motion (the root entity stays at
   * the spawn). False until the skinned character has loaded; the caller
   * falls back to its static framing.
   */
  getHipsWorld(out: pc.Vec3): boolean {
    const hips = this.character?.getHips() ?? null;
    if (!hips) return false;
    out.copy(hips.getPosition());
    return true;
  }

  // ── Network sync ─────────────────────────────────────────────────────

  setTarget(snapshot: RemotePlayerState): void {
    // Sprint 5 — once dead, ignore further snapshots so the corpse stays
    // where it fell and never teleports if the server keeps sending the
    // last health-0 frame.
    if (this.isDead) {
      this.lastKnownHealth = snapshot.health;
      // A snapshot with health > 0 means the round restarted and the
      // server is broadcasting the respawn — wake the body back up.
      if (snapshot.health > 0) this.revive(snapshot);
      return;
    }
    this.targetX = snapshot.x;
    this.targetY = snapshot.y;
    this.targetZ = snapshot.z;
    this.targetYaw = snapshot.yaw;
    this.targetPitch = snapshot.pitch ?? 0;
    this.targetCrouch = snapshot.crouching ? 1 : 0;
    this.reloadingFlag = !!snapshot.reloading;
    this.walkingFlag = !!snapshot.walking;
    this.knifeFlag = snapshot.weaponId === "knife";
    this.crouchFlag = !!snapshot.crouching;
    // Buffer a keyframe for interpolation (see update). Ticks arrive
    // monotonically; duplicates (same snapshot relayed twice) are skipped.
    if (typeof snapshot.tick === "number") {
      const last = this.interpBuf[this.interpBuf.length - 1];
      if (!last || snapshot.tick > last.tick) {
        this.interpBuf.push({
          tick: snapshot.tick,
          x: snapshot.x,
          y: snapshot.y,
          z: snapshot.z,
          yaw: snapshot.yaw,
          pitch: snapshot.pitch ?? 0,
        });
        if (this.interpBuf.length > RemotePlayer.INTERP_BUF_MAX) this.interpBuf.shift();
      }
    }
    // Swap the visible weapon when the opponent changes loadout.
    this.syncWeapon(snapshot.weaponId);
    // Detect death edge.
    if (snapshot.health <= 0 && this.lastKnownHealth > 0) {
      this.beginDeath();
    }
    this.lastKnownHealth = snapshot.health;
  }

  /** Play the grenade-toss animation (driven by grenade_state in the
   *  manager when this peer's grenade first appears in flight). */
  playThrow(): void {
    this.character?.playThrow();
  }

  /**
   * Match-result pose for the end screen: silly dance (won) or defeat
   * slump (lost). The held weapon is hidden — the clips swing the hands,
   * and the rifle's orientation doesn't follow the wrist bone, so it
   * would read as a glitch. Re-applied lazily in tryCreateCharacter()
   * when the skinned character is still loading at show time.
   */
  playResult(won: boolean): void {
    this.resultWon = won;
    this.weaponHolder.enabled = false;
    this.character?.playResult(won);
  }

  /**
   * Halftime side-swap pose for the cinematic mannequin. Weapon hidden for
   * the same reason as playResult (the clips swing the hands freely). Lazily
   * re-applied in tryCreateCharacter() when the skinned character is still
   * loading at show time.
   */
  playHalftime(pose: HalftimePose): void {
    this.halftimePose = pose;
    this.weaponHolder.enabled = false;
    this.character?.playHalftime(pose);
  }

  private beginDeath(): void {
    this.isDead = true;
    this.deathPhase = 0;
    // The corpse freezes where it fell — stale keyframes must not replay.
    this.interpBuf.length = 0;
    // Cache forward axis from current yaw — slide is backward, opposite
    // of where the body is facing.
    const yawDeg = this.root.getEulerAngles().y;
    const yawRad = (yawDeg * Math.PI) / 180;
    this.deathForwardX = -Math.sin(yawRad);
    this.deathForwardZ = -Math.cos(yawRad);
    const p = this.root.getPosition();
    this.deathPosX = p.x;
    this.deathPosZ = p.z;

    // Drop the held weapon from view — a corpse isn't aiming a rifle, and
    // the holder doesn't follow the death slump (it lives on root).
    this.weaponHolder.enabled = false;

    // Skinned character plays its death clip; the primitive rig falls back
    // to the procedural slump (in update) when no character is present.
    this.character?.die();
  }

  private revive(snapshot: RemotePlayerState): void {
    this.isDead = false;
    this.deathPhase = 0;
    // Restore the living body — character back to idle, or re-show the rig.
    this.character?.revive();
    if (!this.usingCharacter && this.placeholder) this.placeholder.enabled = true;
    // Re-show + refresh the held weapon (loadout resets on respawn).
    this.weaponHolder.enabled = true;
    this.targetPitch = snapshot.pitch ?? 0;
    this.currentPitch = this.targetPitch;
    this.syncWeapon(snapshot.weaponId);
    // Restore the rig to upright before the next update lerps it.
    this.visualHolder.setLocalEulerAngles(0, 0, 0);
    this.targetX = snapshot.x;
    this.targetY = snapshot.y;
    this.targetZ = snapshot.z;
    this.targetYaw = snapshot.yaw;
    this.targetCrouch = snapshot.crouching ? 1 : 0;
    // Snap to spawn so the respawn doesn't lerp through the dead pose.
    // The interpolation buffer restarts from the respawn snapshot — old
    // keyframes belong to the previous life.
    this.interpBuf.length = 0;
    if (typeof snapshot.tick === "number") {
      this.interpBuf.push({
        tick: snapshot.tick,
        x: snapshot.x,
        y: snapshot.y,
        z: snapshot.z,
        yaw: snapshot.yaw,
        pitch: snapshot.pitch ?? 0,
      });
    }
    this.root.setPosition(snapshot.x, snapshot.y, snapshot.z);
    this.root.setEulerAngles(0, snapshot.yaw, 0);
    this.prevX = snapshot.x;
    this.prevZ = snapshot.z;
    this.prevY = snapshot.y;
  }

  update(dt: number, renderTick: number | null = null): void {
    // Lazily upgrade to the skinned character once its model finishes loading.
    if (!this.character) this.tryCreateCharacter();

    if (this.isDead) {
      // Skinned character plays its own death clip — nothing to drive.
      if (this.usingCharacter) return;
      // Slump fallback: rotate 90° forward (X-axis tilt) + slide backwards
      // over DEATH_DURATION. Movement halts on the spot afterwards.
      if (this.deathPhase < RemotePlayer.DEATH_DURATION) {
        this.deathPhase = Math.min(RemotePlayer.DEATH_DURATION, this.deathPhase + dt);
        const t = this.deathPhase / RemotePlayer.DEATH_DURATION;
        // Ease-out so the body slumps hard at first then settles.
        const ease = 1 - (1 - t) * (1 - t);
        const tiltDeg = 90 * ease;
        // Slide BACKWARDS (opposite of forward facing) by SLIDE_BACK.
        const slide = RemotePlayer.DEATH_SLIDE_BACK * ease;
        const nx = this.deathPosX - this.deathForwardX * slide;
        const nz = this.deathPosZ - this.deathForwardZ * slide;
        this.root.setPosition(nx, this.targetY, nz);
        // Rotate the visual holder (not root) so the slump tilts the
        // body but doesn't fight the root yaw (corpse keeps its facing).
        this.visualHolder.setLocalEulerAngles(tiltDeg, 0, 0);
      }
      return;
    }

    // ── Position + aim playback ─────────────────────────────────────
    // Preferred path: snapshot-buffer interpolation on the server-tick
    // timeline (see interpBuf docs). Fallback (no clock / no keyframes
    // yet, e.g. the first frames after join): the legacy exponential
    // chase toward the newest snapshot.
    let nx: number;
    let ny: number;
    let nz: number;
    let yawNow: number;
    const buf = this.interpBuf;
    if (renderTick !== null && buf.length > 0) {
      // Drop keyframes that are entirely in the past of the render time,
      // keeping one on each side of it when available.
      while (buf.length >= 2 && buf[1].tick <= renderTick) buf.shift();
      const a = buf[0];
      const b = buf.length >= 2 ? buf[1] : null;
      if (!b || renderTick <= a.tick) {
        // Starved (waiting on the next snapshot) or still behind the
        // oldest keyframe — hold the nearest known pose.
        nx = a.x; ny = a.y; nz = a.z;
        yawNow = a.yaw;
        this.currentPitch = a.pitch;
      } else {
        const span = b.tick - a.tick;
        const t01 = span > 0 ? Math.min(1, (renderTick - a.tick) / span) : 1;
        nx = a.x + (b.x - a.x) * t01;
        ny = a.y + (b.y - a.y) * t01;
        nz = a.z + (b.z - a.z) * t01;
        let dyaw = b.yaw - a.yaw;
        while (dyaw > 180) dyaw -= 360;
        while (dyaw < -180) dyaw += 360;
        yawNow = a.yaw + dyaw * t01;
        this.currentPitch = a.pitch + (b.pitch - a.pitch) * t01;
      }
      this.root.setPosition(nx, ny, nz);
      this.root.setEulerAngles(0, yawNow, 0);
    } else {
      const pos = this.root.getPosition();
      const t = Math.min(1, dt * 12);
      nx = pos.x + (this.targetX - pos.x) * t;
      ny = pos.y + (this.targetY - pos.y) * t;
      nz = pos.z + (this.targetZ - pos.z) * t;
      this.root.setPosition(nx, ny, nz);
      const cur = this.root.getEulerAngles().y;
      let delta = this.targetYaw - cur;
      while (delta > 180) delta -= 360;
      while (delta < -180) delta += 360;
      yawNow = cur + delta * t;
      this.root.setEulerAngles(0, yawNow, 0);
      this.currentPitch += (this.targetPitch - this.currentPitch) * t;
    }

    // Drive the skinned character's animation from rendered velocity +
    // facing. (Hitboxes are server-side, so this is cosmetic.)
    if (this.character) {
      const invDt = dt > 0 ? 1 / dt : 0;
      const vx = (nx - this.prevX) * invDt;
      const vz = (nz - this.prevZ) * invDt;
      const vy = (ny - this.prevY) * invDt;
      // A sharp upward spike means a jump (ramps/stairs rise gently).
      if (vy > 2.0) this.character.playJump();
      this.character.setReloading(this.reloadingFlag);
      this.character.applyMotion(vx, vz, yawNow, this.walkingFlag, this.knifeFlag, this.crouchFlag);
    }
    this.prevX = nx;
    this.prevZ = nz;
    this.prevY = ny;

    // Crouch — squash the primitive rig vertically. Same lerp rate as the
    // local player's camera in FirstPersonController so model + hitbox stay
    // aligned. (No-op visually when the skinned character is in use.)
    this.crouchAmount += (this.targetCrouch - this.crouchAmount) * Math.min(1, dt * 14);
    const heightRatio =
      MOVEMENT_FPS.PLAYER_HEIGHT_CROUCH / MOVEMENT_FPS.PLAYER_HEIGHT_STANDING;
    const yScale = 1 - this.crouchAmount * (1 - heightRatio);
    this.visualHolder.setLocalScale(1, yScale, 1);

    // Held weapon — root already carries yaw; add the aim pitch locally so
    // the barrel (and the muzzle/tracer origin derived from it) points where
    // the opponent is actually looking. currentPitch is driven by the
    // interpolation (or fallback chase) above.
    this.weaponHolder.setLocalEulerAngles(this.currentPitch, 0, 0);

    const hand = this.character?.getRightHand() ?? null;
    if (hand) {
      // Track the animated wrist bone so the weapon reads as truly HELD — it
      // moves with the idle sway / run / reload instead of floating at a fixed
      // body offset. Seat it a little into the curled fingers along the aim
      // frame (the bone is the wrist, behind the grip). Crouch is already
      // baked into the bone's position by the animation.
      const hp = hand.getPosition();
      const g = this.gripCfg;
      const f = this.weaponHolder.forward;
      const u = this.weaponHolder.up;
      const r = this.weaponHolder.right;
      this.weaponHolder.setPosition(
        hp.x + f.x * g.fistFwd + u.x * g.fistUp + r.x * g.fistRight,
        hp.y + f.y * g.fistFwd + u.y * g.fistUp + r.y * g.fistRight,
        hp.z + f.z * g.fistFwd + u.z * g.fistUp + r.z * g.fistRight,
      );
    } else {
      // Pre-character fallback (skinned body still loading): fixed body offset.
      this.weaponHolder.setLocalPosition(
        WEAPON_HAND_X,
        WEAPON_HAND_Y - this.crouchAmount * WEAPON_CROUCH_DROP,
        WEAPON_HAND_Z,
      );
    }
  }

  destroy(): void {
    this.character?.destroy();
    this.root.destroy();
  }
}
